import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Archive, 
  Download, 
  RefreshCw, 
  Search, 
  Calendar,
  TrendingUp,
  Users,
  DollarSign,
  Trophy,
  Filter,
  Eye,
  FileDown,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format as formatDate } from 'date-fns';

interface ArchivedSeason {
  id: string;
  entity_id: string;
  entity_type: string;
  season_name: string;
  season_year: number;
  start_date: string;
  end_date: string;
  archived_at: string;
  archived_by: string;
  summary_stats: any;
  total_games: number;
  total_revenue: number;
  total_participants: number;
  is_active: boolean;
}

interface SeasonArchiveViewerProps {
  entityType?: 'team' | 'venue' | 'coach' | 'all';
  entityId?: string;
  showRestoreOption?: boolean;
}

export const SeasonArchiveViewer = ({ 
  entityType = 'all', 
  entityId,
  showRestoreOption = true 
}: SeasonArchiveViewerProps) => {
  const { user } = useAuth();
  const [archives, setArchives] = useState<ArchivedSeason[]>([]);
  const [filteredArchives, setFilteredArchives] = useState<ArchivedSeason[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>(entityType === 'all' ? 'all' : entityType);
  const [selectedArchive, setSelectedArchive] = useState<ArchivedSeason | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadArchives();
  }, [entityType, entityId]);

  useEffect(() => {
    applyFilters();
  }, [archives, searchQuery, yearFilter, typeFilter]);

  const loadArchives = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('archived_seasons')
        .select('*')
        .order('archived_at', { ascending: false });

      if (entityType !== 'all') {
        query = query.eq('entity_type', entityType);
      }

      if (entityId) {
        query = query.eq('entity_id', entityId);
      }

      const { data, error } = await query;

      if (error) throw error;

      setArchives(data || []);
    } catch (error) {
      console.error('Error loading archived seasons:', error);
      toast.error('Failed to load archived seasons');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...archives];

    // Search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(archive => 
        archive.season_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        archive.entity_type.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Year filter
    if (yearFilter !== 'all') {
      filtered = filtered.filter(archive => 
        archive.season_year.toString() === yearFilter
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(archive => 
        archive.entity_type === typeFilter
      );
    }

    setFilteredArchives(filtered);
  };

  const handleRestore = async (archive: ArchivedSeason) => {
    if (!user) return;

    setRestoring(true);
    try {
      // Create a new season rollover to restore this archived season
      const { error } = await supabase
        .from('season_rollovers')
        .insert({
          entity_type: archive.entity_type,
          entity_id: archive.entity_id,
          old_season_name: 'Current Season',
          new_season_name: archive.season_name,
          initiated_by: user.id,
          rollover_status: 'completed',
          notes: `Restored from archive: ${archive.season_name} (${formatDate(new Date(archive.start_date), 'MMM d, yyyy')} - ${formatDate(new Date(archive.end_date), 'MMM d, yyyy')})`
        });

      if (error) throw error;

      toast.success(`Successfully restored ${archive.season_name}`);
      setSelectedArchive(null);
      await loadArchives();
    } catch (error) {
      console.error('Error restoring season:', error);
      toast.error('Failed to restore season');
    } finally {
      setRestoring(false);
    }
  };

  const handleExport = async (format: 'csv' | 'json') => {
    setExporting(true);
    try {
      if (format === 'json') {
        const dataStr = JSON.stringify(filteredArchives, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `season-archives-${Date.now()}.json`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        // CSV export
        const headers = [
          'Season Name',
          'Entity Type',
          'Season Year',
          'Start Date',
          'End Date',
          'Total Games',
          'Total Revenue',
          'Total Participants',
          'Archived At'
        ];

        const rows = filteredArchives.map(archive => [
          archive.season_name,
          archive.entity_type,
          archive.season_year,
          formatDate(new Date(archive.start_date), 'yyyy-MM-dd'),
          formatDate(new Date(archive.end_date), 'yyyy-MM-dd'),
          archive.total_games,
          `$${archive.total_revenue.toFixed(2)}`,
          archive.total_participants,
          formatDate(new Date(archive.archived_at), 'yyyy-MM-dd HH:mm')
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.join(','))
        ].join('\n');

        const dataBlob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `season-archives-${Date.now()}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      }

      toast.success(`Exported ${filteredArchives.length} archived seasons as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting archives:', error);
      toast.error('Failed to export archives');
    } finally {
      setExporting(false);
    }
  };

  const getAvailableYears = () => {
    const years = [...new Set(archives.map(a => a.season_year))];
    return years.sort((a, b) => b - a);
  };

  const calculateTotals = () => {
    return filteredArchives.reduce(
      (acc, archive) => ({
        games: acc.games + archive.total_games,
        revenue: acc.revenue + archive.total_revenue,
        participants: acc.participants + archive.total_participants
      }),
      { games: 0, revenue: 0, participants: 0 }
    );
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Archive className="w-6 h-6 text-primary" />
              <div>
                <CardTitle>Season Archives</CardTitle>
                <CardDescription>
                  Browse, filter, and restore data from previous seasons
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('csv')}
                disabled={exporting || filteredArchives.length === 0}
              >
                <FileDown className="w-4 h-4 mr-2" />
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('json')}
                disabled={exporting || filteredArchives.length === 0}
              >
                <FileDown className="w-4 h-4 mr-2" />
                JSON
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadArchives}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* Summary Stats */}
        {filteredArchives.length > 0 && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Archive className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">Total Archives</span>
                </div>
                <p className="text-2xl font-bold">{filteredArchives.length}</p>
              </div>

              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">Total Games</span>
                </div>
                <p className="text-2xl font-bold">{totals.games}</p>
              </div>

              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">Total Revenue</span>
                </div>
                <p className="text-2xl font-bold">${totals.revenue.toFixed(2)}</p>
              </div>

              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">Total Participants</span>
                </div>
                <p className="text-2xl font-bold">{totals.participants}</p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="search" className="text-sm font-medium mb-2 block">
                Search
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search seasons..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="year" className="text-sm font-medium mb-2 block">
                Season Year
              </Label>
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger id="year">
                  <SelectValue placeholder="All years" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {getAvailableYears().map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {entityType === 'all' && (
              <div>
                <Label htmlFor="type" className="text-sm font-medium mb-2 block">
                  Entity Type
                </Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger id="type">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="team">Teams</SelectItem>
                    <SelectItem value="venue">Venues</SelectItem>
                    <SelectItem value="coach">Coaches</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setYearFilter('all');
                  setTypeFilter(entityType === 'all' ? 'all' : entityType);
                }}
                className="w-full"
              >
                <Filter className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Archives List */}
      {filteredArchives.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Archive className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {archives.length === 0
                ? 'No archived seasons found'
                : 'No seasons match your filters'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredArchives.map((archive) => (
            <Card key={archive.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{archive.season_name}</CardTitle>
                    <CardDescription>
                      {formatDate(new Date(archive.start_date), 'MMM d, yyyy')} -{' '}
                      {formatDate(new Date(archive.end_date), 'MMM d, yyyy')}
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" className="capitalize">
                    {archive.entity_type}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <Trophy className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                    <p className="font-semibold">{archive.total_games}</p>
                    <p className="text-xs text-muted-foreground">Games</p>
                  </div>
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <Users className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                    <p className="font-semibold">{archive.total_participants}</p>
                    <p className="text-xs text-muted-foreground">Players</p>
                  </div>
                  <div className="text-center p-2 bg-muted/50 rounded">
                    <DollarSign className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                    <p className="font-semibold">${Number(archive.total_revenue).toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">Revenue</p>
                  </div>
                </div>

                <div className="text-xs text-muted-foreground">
                  <Calendar className="w-3 h-3 inline mr-1" />
                  Archived {formatDate(new Date(archive.archived_at), 'MMM d, yyyy')}
                </div>

                <div className="flex gap-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => setSelectedArchive(archive)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Details
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>{archive.season_name}</DialogTitle>
                        <DialogDescription>
                          Archived season details and statistics
                        </DialogDescription>
                      </DialogHeader>
                      <Tabs defaultValue="overview" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="overview">Overview</TabsTrigger>
                          <TabsTrigger value="stats">Statistics</TabsTrigger>
                        </TabsList>
                        <TabsContent value="overview" className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm text-muted-foreground">Entity Type</Label>
                              <p className="font-medium capitalize">{archive.entity_type}</p>
                            </div>
                            <div>
                              <Label className="text-sm text-muted-foreground">Season Year</Label>
                              <p className="font-medium">{archive.season_year}</p>
                            </div>
                            <div>
                              <Label className="text-sm text-muted-foreground">Start Date</Label>
                              <p className="font-medium">
                                {formatDate(new Date(archive.start_date), 'MMM d, yyyy')}
                              </p>
                            </div>
                            <div>
                              <Label className="text-sm text-muted-foreground">End Date</Label>
                              <p className="font-medium">
                                {formatDate(new Date(archive.end_date), 'MMM d, yyyy')}
                              </p>
                            </div>
                            <div>
                              <Label className="text-sm text-muted-foreground">Archived At</Label>
                              <p className="font-medium">
                                {formatDate(new Date(archive.archived_at), 'MMM d, yyyy HH:mm')}
                              </p>
                            </div>
                            <div>
                              <Label className="text-sm text-muted-foreground">Status</Label>
                              <Badge variant={archive.is_active ? 'default' : 'secondary'}>
                                {archive.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                          </div>
                        </TabsContent>
                        <TabsContent value="stats" className="space-y-4">
                          <div className="grid grid-cols-3 gap-4">
                            <Card>
                              <CardContent className="pt-6 text-center">
                                <Trophy className="w-8 h-8 mx-auto mb-2 text-primary" />
                                <p className="text-2xl font-bold">{archive.total_games}</p>
                                <p className="text-sm text-muted-foreground">Total Games</p>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardContent className="pt-6 text-center">
                                <Users className="w-8 h-8 mx-auto mb-2 text-primary" />
                                <p className="text-2xl font-bold">{archive.total_participants}</p>
                                <p className="text-sm text-muted-foreground">Participants</p>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardContent className="pt-6 text-center">
                                <DollarSign className="w-8 h-8 mx-auto mb-2 text-primary" />
                                <p className="text-2xl font-bold">
                                  ${Number(archive.total_revenue).toFixed(2)}
                                </p>
                                <p className="text-sm text-muted-foreground">Revenue</p>
                              </CardContent>
                            </Card>
                          </div>
                          {archive.summary_stats && Object.keys(archive.summary_stats).length > 0 && (
                            <div className="mt-4">
                              <Label className="text-sm font-medium mb-2 block">
                                Additional Statistics
                              </Label>
                              <pre className="bg-muted p-4 rounded-lg text-xs overflow-auto max-h-48">
                                {JSON.stringify(archive.summary_stats, null, 2)}
                              </pre>
                            </div>
                          )}
                        </TabsContent>
                      </Tabs>
                      {showRestoreOption && (
                        <div className="mt-4 pt-4 border-t">
                          <Button
                            onClick={() => handleRestore(archive)}
                            disabled={restoring}
                            className="w-full"
                          >
                            {restoring ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Restoring...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Restore This Season
                              </>
                            )}
                          </Button>
                          <p className="text-xs text-muted-foreground text-center mt-2">
                            This will create a new season rollover with this archived season's data
                          </p>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
